import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { readStoredIds, writeStoredIds } from "@/lib/storedIds";

const MAX_RECENT_ITEMS = 5;
const RECENT_ITEMS_KEY_PREFIX = "forgepoint.recentItems.";

interface RecentItems {
  /** Recently selected item ids, most recent first. */
  recentItemIds: string[];
  /** Records an item as the most recently used one. */
  pushRecentItem: (itemId: string) => void;
}

/**
 * Tracks the items the signed-in user most recently picked, persisted to
 * localStorage under a per-user key.
 * @returns The recent item ids and a handler to record a new one
 */
export function useRecentItems(): RecentItems {
  const userId = useAuthStore((state) => state.user?.id);
  const storageKey = userId ? `${RECENT_ITEMS_KEY_PREFIX}${userId}` : null;
  const [recentItemIds, setRecentItemIds] = useState<string[]>([]);

  useEffect(() => {
    setRecentItemIds(storageKey ? (readStoredIds(storageKey) ?? []) : []);
  }, [storageKey]);

  const pushRecentItem = useCallback(
    (itemId: string) => {
      if (!storageKey) {
        return;
      }
      const next = [
        itemId,
        ...recentItemIds.filter((id) => id !== itemId),
      ].slice(0, MAX_RECENT_ITEMS);
      writeStoredIds(storageKey, next);
      setRecentItemIds(next);
    },
    [recentItemIds, storageKey],
  );

  return { recentItemIds, pushRecentItem };
}
